import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import JSZip from 'jszip';
import './DeckVisualizer.css';

// Import sidebar components
import { 
  DeckStats, 
  CardSearch, 
  CardPreview,
  Recommendations, 
  DrawSimulator, 
  PreloadDecks,
  Tile,
  TileGrid,
  ScrollableActions
} from './sidebar';

// Import DeckArea components
import { DeckControls, WarningBanner } from './DeckArea';
import { 
  ClearConfirmDialog, 
  ImportDeckDialog, 
  ExportCodeDialog, 
  ExportImageDialog,
  SavedDecksDialog
} from './Dialogs';
import HelpDialog from './Dialogs/HelpDialog.shadcn';

// Import language components and hooks
import { LanguageBadge } from './LanguageSelector';
import { useLanguage, useTranslation } from '../contexts/LanguageContext';

// Import hooks
import { useSavedDecks } from '../hooks';
import useCardCatalog from '../hooks/useCardCatalog';
import useCardFilters from '../hooks/useCardFilters';

// Import shared spinner
import SpiralSpinner from './SpiralSpinner';

// Import constants and utilities
import { CHART_OPTIONS, TILE_CONFIG } from '../constants';
import { 
  getMaxCopiesAllowed, 
  getProxyImageUrl, 
  validateDeck, 
  getLevelChartData, 
  getCharacterChartData,
  sortCardsForDisplay,
  normalizeCard,
  shuffleArray,
  expandDeckByCount
} from '../utils';
import { fetchSampleDecks } from '../lib/sanity';
import { sendDeckToRising } from '../services/risingLink';
import { INCLUDE_STAGED } from '../services/cardCatalog';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// How many filtered cards to reveal per scroll page. Filtering is instant over
// the in-memory catalog; this only paces how many card images get painted.
const RESULTS_PAGE_SIZE = 30;

function DeckVisualizer({ initialDeckCode }) {
  // Language context
  const { language } = useLanguage();
  const { t } = useTranslation();
  
  const [cards, setCards] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importCode, setImportCode] = useState('');
  const [exportCode, setExportCode] = useState('');
  const [showExportCode, setShowExportCode] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [previewCard, setPreviewCard] = useState(null);
  const [collapseStats, setCollapseStats] = useState(false); // Start expanded
  const [collapsePreloadDecks, setCollapsePreloadDecks] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [collapseRecommendations, setCollapseRecommendations] = useState(false);
  const [collapseFilters, setCollapseFilters] = useState(true);
  const [collapseDrawSimulator, setCollapseDrawSimulator] = useState(false);
  const [drawnCards, setDrawnCards] = useState([]);
  const [remainingDeck, setRemainingDeck] = useState([]);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mulliganCount, setMulliganCount] = useState(0);
  const [layoutMode, setLayoutMode] = useState('default'); // 'default' or 'expanded'
  const [isMobile, setIsMobile] = useState(false);
  const [exportImageLoading, setExportImageLoading] = useState(false);
  const [sendToRisingLoading, setSendToRisingLoading] = useState(false);
  const [cardViewMode, setCardViewMode] = useState('medium'); // 'large', 'medium', 'details'
  const [showExportImageOptions, setShowExportImageOptions] = useState(false);
  const [exportImageOptions, setExportImageOptions] = useState({
    cardsPerRow: 5,
    numberOfRows: 6
  });
  const [showSavedDecks, setShowSavedDecks] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [cardsReady, setCardsReady] = useState(false);
  const [preloadDecks, setPreloadDecks] = useState([]);

  // Fetch sample decks once on mount for the preload tile.
  useEffect(() => {
    let cancelled = false;
    fetchSampleDecks().then((decks) => {
      if (!cancelled) setPreloadDecks(decks);
    });
    return () => { cancelled = true; };
  }, []);
  
  // Card pool + client-side faceted filtering. The whole catalog is loaded once
  // (cached per language) so filters apply instantly instead of round-tripping
  // to /api/search-cards, which required a query before it would return anything.
  const { catalog, catalogLoading, catalogError } = useCardCatalog();
  const filters = useCardFilters(catalog);
  const { filteredCards } = filters;

  // Filtering is instant, but painting ~1000 card images is not — so results are
  // revealed a page at a time as the user scrolls (see loadMoreResults).
  const [visibleCount, setVisibleCount] = useState(RESULTS_PAGE_SIZE);

  // Any change to the filters is a new result set: rewind to the first page so
  // the user isn't looking at a scroll position from the previous query.
  useEffect(() => {
    setVisibleCount(RESULTS_PAGE_SIZE);
  }, [filteredCards]);

  const visibleResults = useMemo(
    () => filteredCards.slice(0, visibleCount),
    [filteredCards, visibleCount]
  );
  const hasMoreResults = visibleCount < filteredCards.length;

  const loadMoreResults = useCallback(() => {
    setVisibleCount((n) => (n < filteredCards.length ? n + RESULTS_PAGE_SIZE : n));
  }, [filteredCards.length]);

  // Saved decks hook for localStorage persistence
  const {
    savedDecks,
    saveDeck,
    deleteDeck,
    renameDeck,
    saveSessionDeck,
    loadSessionDeck,
    clearSessionDeck,
    maxDecks,
    canSaveMore,
  } = useSavedDecks();

  // Track mobile viewport — switch to tabbed layout below 1100px
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 1100);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Session persistence - save current deck to localStorage whenever it changes
  useEffect(() => {
    // Only save if deck has cards (don't clear session on empty deck deliberately)
    if (cards.length > 0) {
      saveSessionDeck(cards);
    }
  }, [cards, saveSessionDeck]);

  // Load session deck on mount (only if no initialDeckCode provided)
  useEffect(() => {
    if (initialDeckCode) return; // URL code takes precedence
    
    const loadSessionDeckCards = async () => {
      const sessionData = loadSessionDeck();
      if (!sessionData || !sessionData.cards || sessionData.cards.length === 0) {
        setCardsReady(true);
        return;
      }
      
      setSessionLoading(true);
      setCardsReady(false);
      try {
        const cardPromises = sessionData.cards.map(entry =>
          fetch(`/api/cards/${entry.id}?lang=${language}`)
            .then(res => res.json())
            .then(card => {
              const normalized = normalizeCard(card);
              return {
                ...normalized,
                count: entry.count || 1,
                _fallbackLanguage: card.languageMismatch ? card.language : null,
                _availableInLanguage: card.availableInLanguage !== false
              };
            })
            .catch(error => {
              console.error(`Failed to fetch card ${entry.id}:`, error);
              return null;
            })
        );
        
        const fetchedCards = await Promise.all(cardPromises);
        setCards(fetchedCards.filter(Boolean));
      } catch (error) {
        console.error('Failed to restore session deck:', error);
      } finally {
        setSessionLoading(false);
        // Small delay before revealing cards for smooth fade-in
        requestAnimationFrame(() => setCardsReady(true));
      }
    };
    
    loadSessionDeckCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Memoized deck validation
  const deckValidation = useMemo(() => validateDeck(cards), [cards]);
  
  // Count cards that are fallback from another language
  const languageFallbackCount = useMemo(() => {
    return cards.filter(card => card._fallbackLanguage || card._availableInLanguage === false).length;
  }, [cards]);
  
  // Memoized chart data getters
  const levelChartData = useCallback(() => getLevelChartData(deckValidation.stats), [deckValidation.stats]);
  const characterChartData = useCallback(() => getCharacterChartData(deckValidation.stats), [deckValidation.stats]);

  // Load deck from URL parameter on mount
  useEffect(() => {
    const loadDeckFromCode = async (code) => {
      setSessionLoading(true);
      setCardsReady(false);
      try {
        // URL decode first (in case it's double encoded)
        const decodedCode = decodeURIComponent(code);
        const deckString = decodeURIComponent(escape(atob(decodedCode)));
        const deckData = JSON.parse(deckString);
        
        if (deckData.version === 2 && deckData.cards) {
          const cardPromises = deckData.cards.map(entry =>
            fetch(`/api/cards/${entry.id}?lang=${language}`)
              .then(res => res.json())
              .then(card => {
                const normalized = normalizeCard(card);
                return {
                  ...normalized,
                  count: entry.count || 1,
                  _fallbackLanguage: card.languageMismatch ? card.language : null,
                  _availableInLanguage: card.availableInLanguage !== false
                };
              })
              .catch(error => {
                console.error(`Failed to fetch card ${entry.id}:`, error);
                return null;
              })
          );
          
          const fetchedCards = await Promise.all(cardPromises);
          setCards(fetchedCards.filter(Boolean));
        }
      } catch (error) {
        console.error('Failed to load deck from URL:', error);
      } finally {
        setSessionLoading(false);
        requestAnimationFrame(() => setCardsReady(true));
      }
    };

    if (initialDeckCode) {
      loadDeckFromCode(initialDeckCode);
    }
  }, [initialDeckCode, language]);

  // Deck validation and statistics
  // (Moved to utils/deckUtils.js)

  const clearCards = () => {
    setCards([]);
    setShowClearConfirm(false);
    clearSessionDeck(); // Clear session storage when deck is cleared
  };

  // Handle loading a saved deck from localStorage
  const handleLoadSavedDeck = async (deck) => {
    if (!deck.cards || deck.cards.length === 0) return;
    
    setSessionLoading(true);
    setCardsReady(false);
    try {
      const cardPromises = deck.cards.map(entry =>
        fetch(`/api/cards/${entry.id}?lang=${language}`)
          .then(res => res.json())
          .then(card => {
            const normalized = normalizeCard(card);
            return {
              ...normalized,
              count: entry.count || 1,
              _fallbackLanguage: card.languageMismatch ? card.language : null,
              _availableInLanguage: card.availableInLanguage !== false
            };
          })
          .catch(error => {
            console.error(`Failed to fetch card ${entry.id}:`, error);
            return null;
          })
      );
      
      const fetchedCards = await Promise.all(cardPromises);
      setCards(fetchedCards.filter(Boolean));
    } catch (error) {
      console.error('Failed to load saved deck:', error);
    } finally {
      setSessionLoading(false);
      requestAnimationFrame(() => setCardsReady(true));
    }
  };

  // Handle saving current deck to localStorage
  const handleSaveCurrentDeck = (name) => {
    const deckData = {
      version: 2,
      cards: cards.map(card => ({ id: card.id, count: card.count }))
    };
    const code = btoa(unescape(encodeURIComponent(JSON.stringify(deckData))));
    
    saveDeck({
      name,
      cards: cards.map(c => ({ id: c.id, count: c.count })),
      code
    });
  };

  // Hand the current deck to UCG Rising via a `?deck=` deep link. The code is
  // copied to the clipboard either way, so a blocked popup still leaves the
  // player able to paste into Rising's Import box.
  const sendToRising = async () => {
    if (cards.length === 0) {
      alert(t('errors.noCardsToExport') || 'No cards to export!');
      return;
    }

    setSendToRisingLoading(true);
    try {
      const { deckCode, url, opened, copied } = await sendDeckToRising(cards);

      if (!opened) {
        // Popup blocked. The code is on the clipboard if the copy landed;
        // otherwise show the URL so it can still be reached manually.
        alert(
          copied
            ? t('common.deckCodeCopied')
            : `${t('common.sendToRisingHint')}\n\n${url}`
        );
      }

      saveDeck({
        name: t('savedDecks.autoSaveNameCode'),
        cards,
        code: deckCode,
        isAutoSave: true,
        autoSaveType: 'exportCode'
      });
    } catch (error) {
      console.error('Send to Rising failed:', error);
      alert(t('errors.noCardsToExport') || 'Failed to build the deck link');
    } finally {
      setSendToRisingLoading(false);
    }
  };

  const exportDeck = () => {
    if (cards.length === 0) {
      alert(t('errors.noCardsToExport') || 'No cards to export!');
      return;
    }

    try {
      const deckData = {
        version: 2,
        cards: cards.map(card => ({
          id: card.id,
          count: card.count
        }))
      };
      
      const deckString = JSON.stringify(deckData);
      const encodedDeck = btoa(unescape(encodeURIComponent(deckString)));
      setExportCode(encodedDeck);
      setShowExportCode(true);
      
      // Auto-save when exporting code
      saveDeck({
        name: t('savedDecks.autoSaveNameCode'),
        cards: cards,
        code: encodedDeck,
        isAutoSave: true,
        autoSaveType: 'exportCode'
      });
    } catch (error) {
      console.error('Export error:', error);
      alert(t('errors.exportFailed') || 'Failed to export deck');
    }
  };

  const shareDeck = async () => {
    if (cards.length === 0) {
      alert(t('errors.noCardsToExport') || 'No cards to export!');
      return;
    }

    try {
      const deckData = {
        version: 2,
        cards: cards.map(card => ({
          id: card.id,
          count: card.count
        }))
      };
      const deckString = JSON.stringify(deckData);
      const encodedDeck = btoa(unescape(encodeURIComponent(deckString)));
      const shareUrl = `${window.location.origin}/deck-builder?load=${encodeURIComponent(encodedDeck)}`;

      await navigator.clipboard.writeText(shareUrl);
      alert(t('controls.shareLinkCopied', 'Share link copied to clipboard!'));
    } catch (error) {
      console.error('Share error:', error);
      alert(t('errors.shareFailed', 'Failed to generate share link'));
    }
  };

  const importDeck = async (code) => {
    const deckCode = code || importCode;
    if (!deckCode.trim()) {
      alert(t('errors.pasteCodeFirst') || 'Please paste a deck code first!');
      return;
    }

    setSessionLoading(true);
    setCardsReady(false);
    try {
      const deckString = decodeURIComponent(escape(atob(deckCode.trim())));
      const deckData = JSON.parse(deckString);
      
      // Handle both v1 (legacy) and v2 (new) formats
      let importedCards = [];
      
      if (deckData.version === 2 && deckData.cards) {
        // New format with card IDs - fetch full metadata from API with language parameter
        const cardPromises = deckData.cards.map(entry =>
          fetch(`/api/cards/${entry.id}?lang=${language}`)
            .then(res => res.json())
            .then(card => {
              // Normalize card data to ensure feature field is preserved
              const normalized = normalizeCard(card);
              return {
                ...normalized,
                count: entry.count || 1,
                // Track if card is a fallback from another language (use API's languageMismatch flag)
                _fallbackLanguage: card.languageMismatch ? card.language : null,
                _availableInLanguage: card.availableInLanguage !== false
              };
            })
            .catch(error => {
              console.error(`Failed to fetch card ${entry.id}:`, error);
              return null;
            })
        );
        
        const fetchedCards = await Promise.all(cardPromises);
        importedCards = fetchedCards.filter(Boolean);
      } else if (Array.isArray(deckData)) {
        // Legacy format - array of entries
        importedCards = deckData.map(entry => {
          if (entry.id && entry.name) {
            return entry;
          } else if (entry.imageUrl) {
            return {
              id: `legacy-${Date.now()}`,
              imageUrl: entry.imageUrl,
              count: entry.count || 1,
              name: 'Unknown Card'
            };
          }
          return null;
        }).filter(Boolean);
      }

      setCards(importedCards);
      setShowImport(false);
      setImportCode('');
    } catch (error) {
      console.error('Import error:', error);
      alert(t('deck.import.invalidCode') || 'Invalid deck code! Please check and try again.');
    } finally {
      setSessionLoading(false);
      requestAnimationFrame(() => setCardsReady(true));
    }
  };

  const importDeckJson = async (jsonString) => {
    setSessionLoading(true);
    setCardsReady(false);
    try {
      const deckData = JSON.parse(jsonString);
      if (!deckData.cards || !Array.isArray(deckData.cards)) {
        throw new Error('Invalid JSON: missing cards array');
      }

      const cardPromises = deckData.cards.map(entry => {
        // Support both "number" (from JSON export) and "id" formats
        if (entry.number) {
          return fetch(`/api/cards/number/${encodeURIComponent(entry.number)}?lang=${language}`)
            .then(res => {
              if (!res.ok) throw new Error(`Card not found: ${entry.number}`);
              return res.json();
            })
            .then(card => {
              const normalized = normalizeCard(card);
              return {
                ...normalized,
                count: entry.count || 1,
                _fallbackLanguage: card.languageMismatch ? card.language : null,
                _availableInLanguage: card.availableInLanguage !== false
              };
            })
            .catch(error => {
              console.error(`Failed to fetch card ${entry.number}:`, error);
              return null;
            });
        } else if (entry.id) {
          return fetch(`/api/cards/${entry.id}?lang=${language}`)
            .then(res => res.json())
            .then(card => {
              const normalized = normalizeCard(card);
              return {
                ...normalized,
                count: entry.count || 1,
                _fallbackLanguage: card.languageMismatch ? card.language : null,
                _availableInLanguage: card.availableInLanguage !== false
              };
            })
            .catch(error => {
              console.error(`Failed to fetch card ${entry.id}:`, error);
              return null;
            });
        }
        return Promise.resolve(null);
      });

      const fetchedCards = await Promise.all(cardPromises);
      const importedCards = fetchedCards.filter(Boolean);
      setCards(importedCards);
      setShowImport(false);
    } catch (error) {
      console.error('JSON import error:', error);
      alert(t('deck.import.invalidJson', 'Invalid JSON! Please check the format and try again.'));
    } finally {
      setSessionLoading(false);
      requestAnimationFrame(() => setCardsReady(true));
    }
  };

  const copyExport = () => {
    navigator.clipboard.writeText(exportCode).then(() => {
      const btn = document.getElementById('copyBtn');
      btn.textContent = t('common.copied') || 'Copied!';
      setTimeout(() => {
        btn.textContent = t('deck.export.copyCode') || 'Copy Code';
      }, 2000);
    });
  };

  const exportToTTS = async () => {
    if (cards.length === 0) {
      alert(t('errors.noCardsToExport') || 'No cards to export!');
      return;
    }

    const totalCards = cards.reduce((sum, c) => sum + (parseInt(c.count) || 1), 0);
    if (totalCards !== 50) {
      alert((t('errors.deckNeedExactly50') || 'Deck must have exactly 50 cards (current: {count}).').replace('{count}', totalCards));
      return;
    }

    const deckNameInput = prompt(t('deck.export.deckNamePrompt') || 'Name this deck for TTS export (used for file names):', t('deck.export.deckNameDefault') || 'Ultraman Deck');
    if (!deckNameInput) {
      return;
    }
    const safeDeckName = deckNameInput.trim().replace(/[^a-z0-9-_]+/gi, '_') || 'deck';

    try {
      // Use fixed back URL for all cards
      const BACK_URL = "https://steamusercontent-a.akamaihd.net/ugc/16420027240527538/483413ACD3746E956A903173304A14D98BA1A95A/";
      
      // Build CustomDeck, DeckIDs, and ContainedObjects using full-resolution TTS images
      // Image priority for TTS export:
      // 1. images.tts.full - Steam CDN URLs (language-neutral, synced to all language variants)
      // 2. images.blob.full - Azure blob URLs (language-specific, defaults to current locale)
      // 3. images.original.full - Original API URLs (fallback)
      const customDeck = {};
      const deckIDs = [];
      const containedObjects = [];
      let deckIdCounter = 1;

      cards.forEach(card => {
        const count = parseInt(card.count) || 1;
        // Prefer TTS (Steam CDN) images which are synced across all languages
        const faceUrl = card.images?.tts?.full || card.images?.tts?.thumbnail || card.images?.blob?.full || card.images?.blob?.thumbnail || card.images?.original?.full || card.images?.original?.thumbnail;
        
        if (!faceUrl) {
          console.warn(`No image found for card: ${card.name}`);
          return;
        }

        // Scene cards: both Steam CDN and blob images are stored as PORTRAIT PNGs
        // with the scene artwork rotated 90° inside the frame. TTS rotates the
        // card 90° (rotY:90) to display them as landscape on the table.
        const featureValue = (typeof card.feature === 'string'
          ? card.feature
          : card.feature?.value || '').toLowerCase();
        const isScene = featureValue === 'scene';
        const cardRotY = isScene ? 90 : 180.015976;

        const deckKey = deckIdCounter.toString();
        const deckEntry = {
          "FaceURL": faceUrl,
          "BackURL": BACK_URL,
          "NumWidth": 1,
          "NumHeight": 1,
          "BackIsHidden": true,
          "UniqueBack": false,
          "Type": 0
        };

        customDeck[deckKey] = deckEntry;

        for (let i = 0; i < count; i++) {
          const cardId = deckIdCounter * 100 + i;
          deckIDs.push(cardId);
          containedObjects.push({
            "GUID": Math.random().toString(16).slice(2, 8),
            "Name": "Card",
            "Transform": {
              "posX": -38.1626358,
              "posY": 1.69510412,
              "posZ": 13.9254141,
              "rotX": -3.36123918e-07,
              "rotY": cardRotY,
              "rotZ": 2.93983561e-07,
              "scaleX": 1.8,
              "scaleY": 1.0,
              "scaleZ": 1.8
            },
            "Nickname": `${card.name || 'Card'}${card.cardNumber ? ` - ${card.cardNumber}` : card.number ? ` - ${card.number}` : ''}`,
            "Description": "",
            "GMNotes": "",
            "ColorDiffuse": {
              "r": 0.713235259,
              "g": 0.713235259,
              "b": 0.713235259
            },
            "Tags": [
              `Card Type: ${card.type || ''}`,
              `Character Name: ${card.characterName || ''}`,
              `Level: ${card.level || ''}`,
              "limit: ",
              `Rarity: ${card.rarity || ''}`,
              `Type: ${card.subtype || ''}`
            ],
            "LayoutGroupSortIndex": 0,
            "Value": 0,
            "Locked": false,
            "Grid": true,
            "Snap": true,
            "IgnoreFoW": false,
            "MeasureMovement": false,
            "DragSelectable": true,
            "Autoraise": true,
            "Sticky": true,
            "Tooltip": true,
            "GridProjection": false,
            "HideWhenFaceDown": true,
            "Hands": true,
            "CardID": cardId,
            "SidewaysCard": false,
            "CustomDeck": {
              [deckKey]: deckEntry
            },
            "LuaScript": "",
            "LuaScriptState": "",
            "XmlUI": ""
          });
        }

        deckIdCounter++;
      });

      // Build TTS JSON structure
      const ttsData = {
        "SaveName": "",
        "Date": "",
        "VersionNumber": "",
        "GameMode": "",
        "GameType": "",
        "GameComplexity": "",
        "Tags": [],
        "Gravity": 0.5,
        "PlayArea": 0.5,
        "Table": "",
        "Sky": "",
        "Note": "",
        "TabStates": {},
        "LuaScript": "",
        "LuaScriptState": "",
        "XmlUI": "",
        "ObjectStates": [
          {
            "GUID": "4a0f84",
            "Name": "Deck",
            "Transform": {
              "posX": -38.1626358,
              "posY": 1.71205807,
              "posZ": 13.9254141,
              "rotX": -5.1007197e-07,
              "rotY": 180.015976,
              "rotZ": 1.57628949e-06,
              "scaleX": 1.8,
              "scaleY": 1.0,
              "scaleZ": 1.8
            },
            "Nickname": deckNameInput,
            "Description": "",
            "GMNotes": "",
            "ColorDiffuse": {
              "r": 0.713235259,
              "g": 0.713235259,
              "b": 0.713235259
            },
            "LayoutGroupSortIndex": 0,
            "Value": 0,
            "Locked": false,
            "Grid": true,
            "Snap": true,
            "IgnoreFoW": false,
            "MeasureMovement": false,
            "DragSelectable": true,
            "Autoraise": true,
            "Sticky": true,
            "Tooltip": true,
            "GridProjection": false,
            "HideWhenFaceDown": true,
            "Hands": false,
            "SidewaysCard": false,
            "DeckIDs": deckIDs,
            "CustomDeck": customDeck,
            "LuaScript": "",
            "LuaScriptState": "",
            "XmlUI": "",
            "ContainedObjects": containedObjects
          }
        ]
      };

      const zip = new JSZip();
      zip.file(`${safeDeckName}.json`, JSON.stringify(ttsData, null, 2));

      const firstImageUrl = containedObjects[0]?.CustomDeck?.[Object.keys(containedObjects[0]?.CustomDeck || {})[0]]?.FaceURL || cards[0]?.images?.tts?.full;
      if (firstImageUrl) {
        try {
          const response = await fetch(firstImageUrl);
          const arrayBuffer = await response.arrayBuffer();
          
          // Create a 256x256 canvas with transparent background
          const canvas = document.createElement('canvas');
          canvas.width = 256;
          canvas.height = 256;
          const ctx = canvas.getContext('2d');
          
          // Load the image
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = URL.createObjectURL(new Blob([arrayBuffer]));
          });
          
          // Calculate fit dimensions (contain within 256x256)
          const scale = Math.min(256 / img.width, 256 / img.height);
          const fitWidth = img.width * scale;
          const fitHeight = img.height * scale;
          const offsetX = (256 - fitWidth) / 2;
          const offsetY = (256 - fitHeight) / 2;
          
          // Draw image centered on transparent background
          ctx.drawImage(img, offsetX, offsetY, fitWidth, fitHeight);
          
          // Convert to PNG blob
          const pngBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          zip.file(`${safeDeckName}.png`, pngBlob);
          
          URL.revokeObjectURL(img.src);
        } catch (imgErr) {
          console.warn('Failed to fetch first image for zip PNG:', imgErr);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeDeckName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('TTS export error:', error);
      alert((t('errors.ttsExportFailed') || 'Failed to export deck to TTS format') + ': ' + error.message);
    }
  };

  const exportDeckImage = async () => {
    if (cards.length === 0) {
      alert(t('errors.noCardsToExport') || 'No cards to export!');
      return;
    }

    setExportImageLoading(true);
    setShowExportImageOptions(false);
    try {
      // Sort cards the same way as deck view, then build card list with IDs and counts
      const sortedCards = sortCardsForDisplay(cards);
      const cardList = sortedCards.map(card => ({
        cardId: card.cardNumber || card.number,
        count: parseInt(card.count) || 1
      }));

      const res = await fetch('/api/export/deck-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          cards: cardList,
          options: {
            cardsPerRow: exportImageOptions.cardsPerRow,
            numberOfRows: exportImageOptions.numberOfRows,
            cardWidth: 300 // Static card width for consistent quality
          },
          language: language
        })
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(error.error || 'Export failed');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ultraman-deck.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      // Auto-save when exporting image
      saveDeck({
        name: t('savedDecks.autoSaveNameImage'),
        cards: cards.map(c => ({ id: c.id, count: c.count })),
        code: null,
        isAutoSave: true,
        autoSaveType: 'exportImage'
      });
    } catch (error) {
      console.error('Deck image export error:', error);
      alert((t('errors.exportImageFailed') || 'Failed to generate deck image') + ': ' + error.message);
    } finally {
      setExportImageLoading(false);
    }
  };

  const fetchRecommendations = async (deckCards) => {
    if (deckCards.length === 0) {
      setRecommendations([]);
      return;
    }

    try {
      setRecommendationsLoading(true);
      const response = await fetch('/api/recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deckCards, limit: 10, lang: language, includeStaged: INCLUDE_STAGED }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }

      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (error) {
      console.error('Recommendations error:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const drawInitialHand = () => {
    // Create and shuffle deck
    const fullDeck = shuffleArray(expandDeckByCount(cards));

    // Draw first 6 cards
    const hand = fullDeck.slice(0, 6);
    const remaining = fullDeck.slice(6);

    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setHasDrawn(true);
  };

  const mulligan = () => {
    if (mulliganCount >= 1) {
      alert(t('errors.mulliganLimit') || 'You can only mulligan once!');
      return;
    }
    
    if (remainingDeck.length < 6) {
      alert(t('errors.notEnoughCards') || 'Not enough cards left in deck to mulligan!');
      return;
    }

    const hand = remainingDeck.slice(0, 6);
    const remaining = remainingDeck.slice(6);

    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setMulliganCount(mulliganCount + 1);
  };

  const resetDraw = () => {
    setMulliganCount(0);
    drawInitialHand();
  };

  const updateCardCount = (cardId, delta) => {
    const cardIndex = cards.findIndex(c => (c.cardNumber || c.number || c.id) === cardId);
    if (cardIndex === -1) return;
    
    const newCards = [...cards];
    const currentCount = parseInt(newCards[cardIndex].count) || 1;
    const newCount = currentCount + delta;
    const cardNumber = newCards[cardIndex].cardNumber || newCards[cardIndex].number;
    const maxAllowed = getMaxCopiesAllowed(cardNumber);
    
    // Allow removal at 0, enforce max based on custom rules
    if (newCount < 0) return;
    if (newCount > maxAllowed) return;
    
    if (newCount === 0) {
      // Remove card from deck
      newCards.splice(cardIndex, 1);
    } else {
      newCards[cardIndex].count = newCount;
    }
    
    setCards(newCards);
  };

  const addCardFromSearch = (card) => {
    // Check if card already exists in deck by cardNumber (unique identifier)
    const uniqueCardNumber = card.number;
    const existingCardIndex = cards.findIndex(c => (c.cardNumber || c.number) === uniqueCardNumber);
    const maxAllowed = getMaxCopiesAllowed(uniqueCardNumber);
    
    let updatedCards;
    if (existingCardIndex !== -1) {
      // Card already exists - increment count (if not already at max allowed)
      updatedCards = [...cards];
      if (updatedCards[existingCardIndex].count < maxAllowed) {
        updatedCards[existingCardIndex].count += 1;
      }
    } else {
      // New card - add it with normalized data
      const newCard = normalizeCard(card);
      updatedCards = [...cards, newCard];
    }

    setCards(updatedCards);
    // Fetch recommendations based on updated deck
    fetchRecommendations(updatedCards);
  };

  // Chart data is generated via utility functions - see levelChartData and characterChartData callbacks

  return (
    <div className="deck-builder-container h-full p-3 flex flex-col gap-2">
      {/* Dialogs */}
      <ClearConfirmDialog
        open={showClearConfirm}
        onOpenChange={setShowClearConfirm}
        onConfirm={clearCards}
      />
      <ImportDeckDialog
        open={showImport}
        onOpenChange={setShowImport}
        onImport={(code) => {
          importDeck(code);
        }}
        onImportJson={(json) => {
          importDeckJson(json);
        }}
      />
      <ExportCodeDialog
        open={showExportCode}
        onOpenChange={setShowExportCode}
        exportCode={exportCode}
        cards={cards}
      />
      <ExportImageDialog
        open={showExportImageOptions}
        onOpenChange={setShowExportImageOptions}
        onExport={exportDeckImage}
        exportOptions={exportImageOptions}
        onOptionsChange={setExportImageOptions}
        loading={exportImageLoading}
      />
      <SavedDecksDialog
        open={showSavedDecks}
        onOpenChange={setShowSavedDecks}
        savedDecks={savedDecks}
        onLoad={handleLoadSavedDeck}
        onSave={handleSaveCurrentDeck}
        onDelete={deleteDeck}
        onRename={renameDeck}
        canSaveMore={canSaveMore}
        maxDecks={maxDecks}
        currentDeckEmpty={cards.length === 0}
      />
      <HelpDialog
        open={showHelp}
        onOpenChange={setShowHelp}
      />

      {/* Deck Controls Toolbar - above all panels, scrollable on overflow */}
      <ScrollableActions className="flex-shrink-0">
        <DeckControls
          onClear={() => setShowClearConfirm(true)}
          onExport={exportDeck}
          onImport={() => setShowImport(true)}
          onExportImage={() => setShowExportImageOptions(true)}
          onExportTTS={exportToTTS}
          onSendToRising={sendToRising}
          sendToRisingLoading={sendToRisingLoading}
          onCopyCode={copyExport}
          onSavedDecks={() => setShowSavedDecks(true)}
          onShare={shareDeck}
          onHelp={() => setShowHelp(true)}
          exportImageLoading={exportImageLoading}
          showCopyButton={!!exportCode}
          isMobile={isMobile}
          hasSavedDecks={savedDecks.length > 0}
        />
      </ScrollableActions>

      {/* Grid Layout */}
      <TileGrid
        layoutMode={layoutMode}
        onLayoutModeChange={setLayoutMode}
        className="flex-1 min-h-0"
        cardViewMode={cardViewMode}
        isMobile={isMobile}
        t={t}
        searchPaneContent={
          <div className="search-pane-content flex flex-col h-full">
            {/* Search Pane Header */}
            <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-brand to-brand-accent text-white rounded-lg flex-shrink-0 mb-3">
              <span className="font-semibold text-sm">🔍 {t('sidebar.search.title', 'Search Cards')}</span>
            </div>
            <CardSearch
              isCollapsed={false}
              onToggle={() => {}}
              filters={filters}
              visibleResults={visibleResults}
              hasMoreResults={hasMoreResults}
              onResultsScroll={loadMoreResults}
              catalogLoading={catalogLoading}
              catalogError={catalogError}
              onAddCard={(card) => { addCardFromSearch(card); setPreviewCard(card); }}
              onHoverCard={setPreviewCard}
              getProxyImageUrl={getProxyImageUrl}
              collapseFilters={collapseFilters}
              onToggleFilters={() => setCollapseFilters(!collapseFilters)}
              viewMode={cardViewMode}
              onViewModeChange={setCardViewMode}
              t={t}
              hideHeader={true}
            />
          </div>
        }
        rightPaneContent={
          !isMobile ? <CardPreview card={previewCard} className="flex-shrink-0" /> : null
        }
        deckAreaContent={
          <div className="deck-area-content flex flex-col h-full">
            {/* Language fallback warning banner */}
            <WarningBanner
              languageFallbackCount={languageFallbackCount}
              language={language}
            />

            {/* Card Grid */}
            <div className="card-grid flex-1 overflow-auto" id="cardGrid">
              {sessionLoading && (
                <SpiralSpinner size="md" />
              )}
              <div className={`card-grid-inner${cardsReady ? ' cards-visible' : ' cards-hidden'}`}>
              {sortCardsForDisplay(cards).map((card, index) => {
                const cardId = card.cardNumber || card.number || card.id;
                const imageUrl = getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full);
                const rarityText = typeof card.rarity === 'string' ? card.rarity : card.rarity?.value || '';
                const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
                const featureValue = card.feature || '';
                const isScene = featureValue === 'scene';
                const isHovered = hoveredCardIndex === cardId;
                const cardLanguage = card.language || card._fallbackLanguage;
                
                return (
                  <div 
                    key={cardId} 
                    className={`card-wrapper${isScene ? ' scene-card' : ''}${card._fallbackLanguage ? ' language-fallback' : ''}`}
                    onMouseEnter={() => { setHoveredCardIndex(cardId); setPreviewCard(card); }}
                    onMouseLeave={() => { setHoveredCardIndex(null); }}
                  >
                    {imageUrl && <img src={imageUrl} alt={card.name} className="card-image" />}
                    <LanguageBadge cardLanguage={cardLanguage} selectedLanguage={language} />
                    <div className="card-info">
                      <div className="card-name">{card.name}</div>
                      <div className="card-meta">{card.number}</div>
                      {rarityText && <div className="card-rarity">{rarityText}</div>}
                      {typeText && <div className="card-type">{typeText}</div>}
                    </div>
                    <div className="card-count">x{card.count}</div>
                    
                    {isHovered && (
                      <div className="card-overlay">
                        <div className="overlay-content">
                          <h4>{card.name}</h4>
                          {card.number && <p>{card.number}</p>}
                          <div className="overlay-controls">
                            <button 
                              className="count-btn decrease-btn"
                              onClick={() => updateCardCount(cardId, -1)}
                              title="Decrease count or remove"
                            >
                              −
                            </button>
                            <span className="count-display hidden sm:inline">x{card.count}</span>
                            <button 
                              className="count-btn increase-btn"
                              onClick={() => updateCardCount(cardId, 1)}
                              disabled={card.count >= getMaxCopiesAllowed(card.cardNumber || card.number)}
                              title="Increase count"
                            >
                              +
                            </button>
                          </div>
                          <button
                            className="delete-card-btn"
                            onClick={() => updateCardCount(cardId, -card.count)}
                            title="Remove card from deck"
                          >
                            <span className="sm:hidden">Remove</span><span className="hidden sm:inline">🗑 Remove Card</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        }
      >
        {/* Tile: Deck Stats (utility tile in right pane) */}
        <Tile
          id={TILE_CONFIG.deckStats.id}
          icon={TILE_CONFIG.deckStats.icon}
          title={t(TILE_CONFIG.deckStats.titleKey) || TILE_CONFIG.deckStats.defaultTitle}
          isCollapsed={collapseStats}
          onToggle={() => setCollapseStats(!collapseStats)}
          heightUnits={TILE_CONFIG.deckStats.defaultHeight}
          minHeight={TILE_CONFIG.deckStats.minHeight}
          maxHeight={TILE_CONFIG.deckStats.maxHeight}
          collapsedContent={
            <DeckStats
              isCollapsed={true}
              onToggle={() => {}}
              deckValidation={deckValidation}
              getLevelChartData={levelChartData}
              getCharacterChartData={characterChartData}
              chartOptions={CHART_OPTIONS}
              cards={cards}
              t={t}
              hideHeader={true}
            />
          }
        >
          <DeckStats
            isCollapsed={false}
            onToggle={() => {}}
            deckValidation={deckValidation}
            getLevelChartData={levelChartData}
            getCharacterChartData={characterChartData}
            chartOptions={CHART_OPTIONS}
            cards={cards}
            t={t}
            hideHeader={true}
          />
        </Tile>

        {/* Tile: Draw Simulator (utility tile in right pane) */}
        <Tile
          id={TILE_CONFIG.drawSimulator.id}
          icon={TILE_CONFIG.drawSimulator.icon}
          title={t(TILE_CONFIG.drawSimulator.titleKey) || TILE_CONFIG.drawSimulator.defaultTitle}
          isCollapsed={collapseDrawSimulator}
          onToggle={() => setCollapseDrawSimulator(!collapseDrawSimulator)}
          heightUnits={TILE_CONFIG.drawSimulator.defaultHeight}
          minHeight={TILE_CONFIG.drawSimulator.minHeight}
          maxHeight={TILE_CONFIG.drawSimulator.maxHeight}
        >
          <DrawSimulator
            isCollapsed={false}
            onToggle={() => {}}
            drawnCards={drawnCards}
            remainingDeck={remainingDeck}
            hasDrawn={hasDrawn}
            mulliganCount={mulliganCount}
            deckHasCards={cards.length > 0}
            onDrawInitialHand={drawInitialHand}
            onMulligan={mulligan}
            onResetDraw={resetDraw}
            getProxyImageUrl={getProxyImageUrl}
            t={t}
            hideHeader={true}
          />
        </Tile>

        {/* Tile: Recommendations (utility tile in right pane, if enabled) */}
        {!TILE_CONFIG.recommendations.disabled && (
          <Tile
            id={TILE_CONFIG.recommendations.id}
            icon={TILE_CONFIG.recommendations.icon}
            title={t(TILE_CONFIG.recommendations.titleKey) || TILE_CONFIG.recommendations.defaultTitle}
            isCollapsed={collapseRecommendations}
            onToggle={() => setCollapseRecommendations(!collapseRecommendations)}
            heightUnits={TILE_CONFIG.recommendations.defaultHeight}
            minHeight={TILE_CONFIG.recommendations.minHeight}
            maxHeight={TILE_CONFIG.recommendations.maxHeight}
          >
            <Recommendations
              isCollapsed={false}
              onToggle={() => {}}
              recommendations={recommendations}
              loading={recommendationsLoading}
              onAddCard={addCardFromSearch}
              getProxyImageUrl={getProxyImageUrl}
              deckHasCards={cards.length > 0}
              sidebarExpanded={true}
              viewMode={cardViewMode}
              t={t}
              hideHeader={true}
            />
          </Tile>
        )}

        {/* Tile: Sample Decks (utility tile in right pane, if enabled) */}
        {!TILE_CONFIG.preloadDecks.disabled && (
          <Tile
            id={TILE_CONFIG.preloadDecks.id}
            icon={TILE_CONFIG.preloadDecks.icon}
            title={t(TILE_CONFIG.preloadDecks.titleKey) || TILE_CONFIG.preloadDecks.defaultTitle}
            isCollapsed={collapsePreloadDecks}
            onToggle={() => setCollapsePreloadDecks(!collapsePreloadDecks)}
            heightUnits={TILE_CONFIG.preloadDecks.defaultHeight}
            minHeight={TILE_CONFIG.preloadDecks.minHeight}
            maxHeight={TILE_CONFIG.preloadDecks.maxHeight}
            className="flex-1"
          >
            <PreloadDecks
              isCollapsed={false}
              onToggle={() => {}}
              preloadDecks={preloadDecks}
              language={language}
              t={t}
              hideHeader={true}
              onLoadDeck={async (deck) => {
                if (deck.deckCode) {
                  setSessionLoading(true);
                  setCardsReady(false);
                  try {
                    const deckString = decodeURIComponent(escape(atob(deck.deckCode.trim())));
                    const deckData = JSON.parse(deckString);
                    
                    if (deckData.version === 2 && deckData.cards) {
                      const cardPromises = deckData.cards.map(entry =>
                        fetch(`/api/cards/${entry.id}?lang=${language}`)
                          .then(res => res.json())
                          .then(card => {
                            const normalized = normalizeCard(card);
                            return { 
                              ...normalized, 
                              count: entry.count || 1,
                              _fallbackLanguage: card.languageMismatch ? card.language : null,
                              _availableInLanguage: card.availableInLanguage !== false
                            };
                          })
                          .catch(() => null)
                      );
                      const fetchedCards = await Promise.all(cardPromises);
                      setCards(fetchedCards.filter(Boolean));
                      setLayoutMode('default');
                    }
                  } catch (error) {
                    console.error('Failed to load deck:', error);
                    alert(t('errors.importFailed') || 'Failed to load deck');
                  } finally {
                    setSessionLoading(false);
                    requestAnimationFrame(() => setCardsReady(true));
                  }
                } else {
                  alert(t('sidebar.sampleDecks.comingSoon') || 'This deck is not yet available.');
                }
              }}
            />
          </Tile>
        )}
      </TileGrid>
    </div>
  );
}

export default DeckVisualizer;
